<template>
  <div class="card card-container">
    <div class="tw-py-10">
      <div class="tw-flex tw-items-center tw-justify-between tw-flex-wrap">
        <div class="tw-flex tw-items-center">
          <h2 class="page-title">
            Company Calendars
          </h2>
        </div>
        <div class="tw-flex tw-flex-wrap">
          <button
            class="btn-link tw-flex tw-items-center"
            @click="showConnectionsModal"
          >
            <SvgIcon name="connection" class="tw-w-4 tw-h-4 tw-mr-1" />
            <span class="tw-font-medium">Manage connections</span>
          </button>
          <SpinnerButton
            data-cy="btn-add-calendar"
            type="button"
            class="tw-ml-3"
            @click="showCreateCalendarModal"
          >
            Add Calendar
          </SpinnerButton>
        </div>
      </div>
    </div>

    <div>
      <p class="tw-mb-2">
        Import leave information into a shared company calendar in Outlook or
        Google.
      </p>
      <p class="tw-mb-2">
        Leave Dates will create one calendar event per day showing who is on
        leave.

        <BaseLink :href="companyCalendarDocumentationUrl">More info</BaseLink>
      </p>
      <div v-scroll-hint class="table-responsive  settings-table">
        <Loading
          loader="dots"
          :is-full-page="false"
          :active="loadingCompanyCalendars"
        />
        <CompanyCalendarTable
          v-if="!loadingCompanyCalendars"
          :company-calendars="companyCalendars"
          @reconnect="reconnect"
          @edit-calendar="editCalendar"
          @delete-calendar="deleteCalendar"
        />
      </div>
    </div>

    <Modal
      id="modal-company-calendar"
      :classes="[
        'tw-shadow-md',
        'tw-bg-white',
        'tw-rounded-lg',
        'modal-overflow-visible',
      ]"
      :max-width="480"
      name="modal-company-calendar"
      width="95%"
      height="auto"
      adaptive
      scrollable
      @before-close="onCloseCalendarIntegrationModal"
    >
      <div class="modal-header">
        <div class="tw-flex tw-justify-between">
          <div>
            <div class="modal-title">
              {{ editing ? 'Edit' : 'New' }} Company Calendar
            </div>
          </div>
          <div data-cy="edit-provider-close">
            <button class="modal-close" @click="hideCompanyCalendarModal">
              <SvgIcon name="close" class="tw-w-4 tw-h-4" />
            </button>
          </div>
        </div>
      </div>

      <div class="tw-mt-3 tw-p-3">
        <form
          id="company-calendar-create-form"
          class="tw-w-full"
          @submit.prevent="submit"
        >
          <div class="form-group">
            <div class="tw-w-full tw-px-3">
              <label class="form-label" for="calendar-service">
                Calendar Type<span class="required-field">&#42;</span>
              </label>
              <div class="tw-relative">
                <vSelect
                  id="calendar-service"
                  v-model="form.service"
                  v-validate="'required'"
                  :options="services"
                  :disabled="editing"
                  :show-labels="false"
                  :tabindex="1"
                  :allow-empty="false"
                  :max-height="180"
                  placeholder="Select a calendar type"
                  name="type"
                  label="name"
                  track-by="id"
                >
                </vSelect>
              </div>
            </div>
          </div>

          <div class="form-group">
            <div class="tw-w-full tw-px-3">
              <label class="form-label" for="account">
                Account <span class="required-field">&#42;</span>
              </label>
              <div class="tw-relative">
                <vSelect
                  id="account"
                  v-model="form.account"
                  v-validate="'required'"
                  :options="accounts"
                  :disabled="editing"
                  :show-labels="false"
                  :tabindex="2"
                  :allow-empty="false"
                  :max-height="180"
                  placeholder="Select an account"
                  name="account"
                >
                </vSelect>
              </div>
            </div>
          </div>

          <div class="form-group">
            <div class="tw-w-full tw-px-3">
              <label class="form-label" for="company-calendar">
                Calendar <span class="required-field">&#42;</span>
              </label>
              <div class="tw-relative">
                <vSelect
                  id="company-calendar"
                  v-model="form.selectedCalendar"
                  v-validate="'required'"
                  :options="accountCalendars"
                  :show-labels="false"
                  :disabled="editing"
                  :tabindex="1"
                  :allow-empty="false"
                  :max-height="180"
                  placeholder="Select a calendar"
                  name="type"
                  label="name"
                  track-by="id"
                >
                </vSelect>
              </div>
            </div>
          </div>

          <div class="form-group">
            <div class="tw-w-full tw-px-3">
              <label class="form-label" for="scopes-picker">
                Show <span class="required-field">&#42;</span>
              </label>
              <VisibilityScopesPicker
                id="scopes-picker"
                v-model="form.scopes"
                :departments="departments"
                :except-their-own-data="true"
              />
            </div>
          </div>

          <div class="form-group">
            <div class="tw-w-full tw-px-3">
              <label class="form-label" for="leave-types-picker">
                Leave Types <span class="required-field">&#42;</span>
              </label>
              <MultipleLeaveTypesPicker
                id="leave-types-picker"
                v-model="form.leave_types"
                :leave-types="leaveTypes"
              />
            </div>
          </div>

          <div class="form-group">
            <div class="tw-w-full tw-px-3">
              <label class="form-label" for="event-title-prefix">
                Event Title <span class="required-field">&#42;</span>
              </label>
              <div class="tw-flex tw-w-full tw-space-x-6">
                <div class="tw-w-1/3 tw-flex-grow">
                  <input
                    id="event-title-prefix"
                    v-model="form.event_title_prefix"
                    v-validate="'required|max:20'"
                    maxlength="20"
                    name="title"
                    class="form-control"
                    type="text"
                    tabindex="3"
                    autocomplete="off"
                  />
                  <p
                    v-show="errors.has('title')"
                    class="tw-mt-1 tw-text-red-700 tw-text-sm"
                  >
                    {{ errors.first('title') }}
                  </p>
                </div>
                <div class="tw-w-2/3 tw-relative">
                  <vSelect
                    id="event-title-format"
                    v-model="form.event_title_format"
                    v-validate="'required'"
                    :options="titleFormats"
                    :show-labels="false"
                    :tabindex="4"
                    :allow-empty="false"
                    :max-height="180"
                    name="type"
                    label="name"
                    track-by="id"
                  >
                  </vSelect>
                </div>
              </div>
            </div>
          </div>

          <div class="tw-flex tw-flex-wrap tw-mb-3">
            <div class="tw-w-full tw-px-3">
              <div class="tw-flex tw-justify-end">
                <SpinnerButton
                  :disabled="
                    !valid ||
                      loading ||
                      !hasSelectedScopes ||
                      !hasSelectedCalendarService ||
                      !hasSelectedLeaveTypes
                  "
                  :loading="loading"
                  :spinner-only="true"
                  type="submit"
                >
                  {{ editing ? 'Update' : 'Create' }}
                </SpinnerButton>
              </div>
            </div>
          </div>
        </form>
      </div>
    </Modal>

    <Modal
      id="modal-external-calendar-connections"
      :classes="[
        'tw-shadow-md',
        'tw-bg-white',
        'tw-rounded-lg',
        'modal-overflow-visible',
      ]"
      name="modal-external-calendar-connections"
      height="auto"
      width="800"
      adaptive
      scrollable
    >
      <div class="modal-header">
        <div class="tw-flex tw-justify-between">
          <div>
            <div class="modal-title">
              Manage connections
            </div>
          </div>
          <div data-cy="edit-provider-close">
            <button class="modal-close" @click="hideConnectionsModal">
              <SvgIcon name="close" class="tw-w-4 tw-h-4" />
            </button>
          </div>
        </div>
      </div>

      <div class="tw-p-6">
        <div class="tw-flex tw-items-center">
          <vSelect
            id="calendar-service"
            v-model="form.service"
            :options="services"
            :show-labels="false"
            :allow-empty="false"
            :max-height="180"
            placeholder="Select provider"
            name="type"
            label="name"
            class="tw-w-64"
          >
          </vSelect>
          <SpinnerButton
            class="btn tw-ml-2 tw-h-12"
            :disabled="!form.service || reconnecting || deleting"
            :loading="loading"
            @click="connectExternalCalendarConnection"
          >
            Connect
          </SpinnerButton>
        </div>

        <hr class="tw-mt-5" />
        <table class="ld-table ld-table-striped">
          <thead>
            <tr>
              <th>Provider</th>
              <th>Account</th>
              <th>Status</th>
              <th style="width:60px"></th>
            </tr>
          </thead>
          <tbody>
            <template v-if="employmentExternalCalendarConnections.all().length">
              <template
                v-for="connection in employmentExternalCalendarConnections.all()"
              >
                <tr :key="connection.id">
                  <td>
                    <span class="tw-font-medium">{{
                      getServiceNameByProvider(connection.provider)
                    }}</span>
                  </td>
                  <td>{{ connection.email }}</td>
                  <td>
                    <span
                      v-if="connection.is_active"
                      class="tw-inline-flex tw-bg-green-200 tw-rounded-full tw-text-center tw-px-5 tw-py-1"
                    >
                      <span class="tw-text-green-500 tw-font-semibold"
                        >Connected</span
                      >
                    </span>
                    <div v-else>
                      <span
                        class="tw-inline-flex tw-bg-red-200 tw-rounded-full tw-text-center tw-px-5 tw-py-1"
                      >
                        <span class="tw-text-red-500 tw-font-semibold"
                          >Disconnected</span
                        >
                      </span>
                      <button
                        class="btn btn-link"
                        :disabled="reconnecting || deleting"
                        @click="reconnect(connection)"
                      >
                        Reconnect
                      </button>
                    </div>
                  </td>
                  <td class="tw-flex tw-justify-end">
                    <SpinnerButton
                      class="btn btn-icon tw-border tw-bg-red-500 hover:tw-bg-red-500 tw-border-red-500 tw-flex tw-items-center tw-justify-center"
                      type="button"
                      :loading="deleting"
                      :spinner-only="true"
                      :disabled="reconnecting || deleting"
                      @click="deleteConnection(connection)"
                    >
                      <SvgIcon
                        name="trash"
                        class="svg-icon tw-w-3 tw-h-3 tw-text-white"
                      />
                    </SpinnerButton>
                  </td>
                </tr>

                <!-- Shared Mailboxes Section (Outlook only) -->
                <template v-if="connection.provider === 'outlook'">
                  <tr
                    v-for="mailbox in connection.shared_mailboxes || []"
                    :key="`mailbox-${mailbox.id}`"
                    class="tw-bg-gray-50"
                  >
                    <td class="tw-pl-8">
                      <span class="tw-text-gray-500">↳</span>
                      <span class="tw-ml-1 tw-text-sm">Shared Mailbox</span>
                    </td>
                    <td>{{ mailbox.email }}</td>
                    <td>
                      <span
                        v-if="mailbox.is_active"
                        class="tw-inline-flex tw-bg-green-200 tw-rounded-full tw-text-center tw-px-5 tw-py-1"
                      >
                        <span
                          class="tw-text-green-500 tw-font-semibold tw-text-sm"
                          >Connected</span
                        >
                      </span>
                      <div v-else>
                        <span
                          class="tw-inline-flex tw-bg-red-200 tw-rounded-full tw-text-center tw-px-5 tw-py-1"
                          :title="mailbox.service_error?.message"
                        >
                          <span
                            class="tw-text-red-500 tw-font-semibold tw-text-sm"
                            >Error</span
                          >
                        </span>
                      </div>
                    </td>
                    <td class="tw-flex tw-justify-end">
                      <SpinnerButton
                        class="btn btn-icon tw-border tw-bg-red-500 hover:tw-bg-red-500 tw-border-red-500 tw-flex tw-items-center tw-justify-center"
                        type="button"
                        :loading="removingSharedMailbox"
                        :spinner-only="true"
                        :disabled="
                          reconnecting || deleting || removingSharedMailbox
                        "
                        @click="removeSharedMailbox(connection, mailbox)"
                      >
                        <SvgIcon
                          name="trash"
                          class="svg-icon tw-w-3 tw-h-3 tw-text-white"
                        />
                      </SpinnerButton>
                    </td>
                  </tr>

                  <!-- Add Shared Mailbox Input -->
                  <tr
                    v-if="connection.is_active"
                    :key="`add-mailbox-${connection.id}`"
                    class="tw-bg-gray-50"
                  >
                    <td class="tw-pl-8">
                      <span class="tw-text-gray-500">↳</span>
                    </td>
                    <td colspan="2">
                      <div class="tw-flex tw-items-center tw-gap-2">
                        <input
                          v-model="sharedMailboxEmails[connection.id]"
                          type="email"
                          placeholder="shared-mailbox@company.com"
                          class="form-control tw-flex-1"
                          :disabled="addingSharedMailbox"
                          @keyup.enter="addSharedMailbox(connection)"
                        />
                        <SpinnerButton
                          class="btn btn-sm"
                          :disabled="
                            !sharedMailboxEmails[connection.id] ||
                              addingSharedMailbox
                          "
                          :loading="addingSharedMailbox"
                          :spinner-only="true"
                          @click="addSharedMailbox(connection)"
                        >
                          Add
                        </SpinnerButton>
                      </div>
                      <p
                        v-if="sharedMailboxErrors[connection.id]"
                        class="tw-text-red-500 tw-text-sm tw-mt-1"
                      >
                        {{ sharedMailboxErrors[connection.id] }}
                      </p>
                    </td>
                    <td></td>
                  </tr>
                </template>
              </template>
            </template>
            <tr v-else>
              <td class="tw-bg-white" colspan="5">
                <NoData :message="'No connections has been added'" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </Modal>
  </div>
</template>

<script>
import { find, omit, reduce } from 'lodash-es'
import Loading from 'vue-loading-overlay'
import BaseLink from '@/components/BaseLink'
import { Departments, LeaveTypes } from '@/api'
import ValidatesForm from '@/mixins/ValidatesForm'
import SpinnerButton from '@/components/SpinnerButton'
import documentationUrls from '@/documentations/documentation-urls'
import ExternalCalendars from '@/api/externalcalendars/ExternalCalendars'
import MultipleLeaveTypesPicker from '@/components/MultipleLeaveTypesPicker'
import VisibilityScopesPicker from '@/components/departments/VisibilityScopesPicker'
import CompanyCalendarTable from '@/components/integrations/company-calendar/CompanyCalendarTable'
import OAuthProvider from '@/api/oauth/OAuthProvider'
import EmploymentExternalCalendarConnections from '@/models/Synchronizations/EmploymentExternalCalendarConnections'
import NoData from '@/components/NoData'

const vSelect = () => import('vue-multiselect')

const SERVICES = [
  {
    id: 'google_calendar',
    name: 'Google',
    provider: 'google',
  },
  {
    id: 'outlook_calendar',
    name: 'Outlook',
    provider: 'outlook',
  },
]

const TITLE_FORMATS = [
  {
    id: 'employee_initials',
    name: 'List of employee initials',
  },
  {
    id: 'employee_full_names',
    name: 'List of employee full names',
  },
  {
    id: 'employee_codes',
    name: 'List of employee codes',
  },
]

const DEFAULT_LEAVE_TYPE = {
  id: '',
  name: 'All',
}

export default {
  name: 'CompanyCalendars',

  components: {
    NoData,
    Loading,
    vSelect,
    CompanyCalendarTable,
    VisibilityScopesPicker,
    SpinnerButton,
    BaseLink,
    MultipleLeaveTypesPicker,
  },

  mixins: [ValidatesForm],

  data() {
    return {
      editing: false,
      loading: true,
      deleting: false,
      reconnecting: false,
      addingSharedMailbox: false,
      removingSharedMailbox: false,
      sharedMailboxEmails: {},
      sharedMailboxErrors: {},
      loadingCompanyCalendars: true,
      departments: [],
      services: SERVICES,
      titleFormats: TITLE_FORMATS,
      calendarSynchronizations: [],
      availableCalendars: [],
      form: {
        service: null,
        account: null,
        scopes: null,
        event_title_prefix: null,
        event_title_format: null,
        leave_types: [],
        selectedCalendar: null,
      },
      leaveTypes: [DEFAULT_LEAVE_TYPE],
      employmentExternalCalendarConnections: new EmploymentExternalCalendarConnections(),
    }
  },

  computed: {
    companyCalendarDocumentationUrl() {
      return documentationUrls.companyCalendarUrl
    },

    accounts() {
      if (!this.form.service || !this.activeConnections) {
        return []
      }

      return this.activeConnections.map(account => account.email)
    },

    accountCalendars() {
      if (!this.form.account || !this.activeConnections) {
        return []
      }

      const connection = this.activeConnections.find(
        account => account.email === this.form.account
      )

      if (!connection) return []

      const personalCalendars = (connection.calendars ?? []).map(cal => ({
        ...cal,
        shared_mailbox_id: null,
      }))

      const sharedCalendars = []
      if (connection.provider === 'outlook' && connection.shared_mailboxes) {
        connection.shared_mailboxes
          .filter(mailbox => mailbox.is_active)
          .forEach(mailbox => {
            mailbox.calendars.forEach(cal => {
              sharedCalendars.push({
                ...cal,
                name: `${cal.name} (${mailbox.email})`,
                shared_mailbox_id: mailbox.id,
              })
            })
          })
      }

      return [...personalCalendars, ...sharedCalendars]
    },

    activeConnections() {
      if (!this.form.service) {
        return []
      }

      return (
        this.employmentExternalCalendarConnections
          .filterByProvider(this.form.service.provider)
          .filter(connection => connection.is_active) ?? []
      )
    },

    hasSelectedScopes() {
      return this.form.scopes && this.form.scopes.length > 0
    },

    hasSelectedCalendarService() {
      return !!this.form.service
    },

    hasSelectedLeaveTypes() {
      return !!this.form.leave_types
    },

    companyCalendars() {
      return this.calendarSynchronizations.map(synchronization => {
        const connection = synchronization.external_calendar_connection

        let sharedMailboxEmail = null
        if (synchronization.shared_mailbox_id) {
          const fullConnection = this.employmentExternalCalendarConnections.findById(
            connection.id
          )
          const mailbox = fullConnection?.shared_mailboxes?.find(
            m => m.id === synchronization.shared_mailbox_id
          )
          sharedMailboxEmail = mailbox?.email
        }

        return {
          ...synchronization,
          serviceName: this.getServiceNameByProvider(connection.provider),
          scopeNames: this.getScopesNames(synchronization.scopes).join(', '),
          calendarName: this.getCalendarName(synchronization),
          sharedMailboxEmail,
        }
      })
    },
  },

  watch: {
    '$route.query': {
      immediate: true,
      async handler(newVal, oldVal) {
        if (newVal === oldVal) return

        if (newVal['show-company-calendar']) {
          this.$nextTick(() => {
            this.resetCompanyCalendarModal()

            this.$modal.show('modal-company-calendar')
          })
        }
      },
    },

    '$route.query.company': {
      async handler(newVal, oldVal) {
        if (newVal === oldVal) return

        await this.loadInitialData()
      },
    },

    'form.service'() {
      if (this.editing) return

      this.form.account = null
      this.form.selectedCalendar = null
    },

    'form.account'() {
      if (this.editing) return

      this.form.selectedCalendar = null
    },
  },

  async mounted() {
    await this.loadInitialData()

    if (this.$route.query.error) {
      this.showErrorMessage()
    }
  },

  methods: {
    async loadInitialData() {
      await this.fetchEmploymentExternalCalendarConnections()

      await Promise.all([
        this.fetchCalendarSynchronizations(),
        this.fetchDepartments(),
        this.fetchLeaveTypes(),
      ])
    },

    async fetchEmploymentExternalCalendarConnections() {
      this.loadingCompanyCalendars = true

      this.employmentExternalCalendarConnections = await ExternalCalendars.getEmploymentExternalCalendarConnections(
        this.activeEmployment.id,
        this.activeCompany.id
      )

      this.loadingCompanyCalendars = false
    },

    async fetchDepartments() {
      try {
        const { data } = await Departments.all(this.$route.query)

        this.departments = data
      } catch ({ response }) {
        this.validateFromResponse(response, false)
      }
    },

    async fetchLeaveTypes() {
      this.loading = true
      const data = await LeaveTypes.all(this.$apollo, this.activeCompany.id)

      this.leaveTypes = [DEFAULT_LEAVE_TYPE, ...data]
      this.loading = false
    },

    showConnectionsModal() {
      this.$modal.show('modal-external-calendar-connections')
    },

    hideConnectionsModal() {
      this.$modal.hide('modal-external-calendar-connections')
    },

    async connectExternalCalendarConnection() {
      this.loading = true

      try {
        const redirectUrl =
          window.location.origin +
          this.$router.resolve({ name: 'integrations' }).href +
          `?company=${this.activeCompany.id}&show-company-calendar=true`

        const { data } = await OAuthProvider.redirect(this.form.service.id, {
          company_id: this.activeCompany.id,
          redirect_url: redirectUrl,
        })

        window.location.href = data.redirect_url
      } catch ({ response }) {
        this.validateFromResponse(response)
      }

      this.loading = false
    },

    showCreateCalendarModal() {
      this.resetCompanyCalendarModal()

      this.editing = false

      this.$modal.show('modal-company-calendar')
    },

    hideCompanyCalendarModal() {
      this.$modal.hide('modal-company-calendar')

      this.resetCompanyCalendarModal()
    },

    onCloseCalendarIntegrationModal() {
      if (!this.$route.query.hasOwnProperty('show-company-calendar')) return

      this.$router.push({
        query: {
          ...omit(this.$route.query, 'show-company-calendar'),
        },
      })
    },

    editCalendar(calendar) {
      this.editing = true

      const service = this.services.find(
        service =>
          service.provider === calendar.external_calendar_connection.provider
      )

      const connection = this.employmentExternalCalendarConnections.findById(
        calendar.external_calendar_connection.id
      )

      let selectedCalendar = connection?.calendars.find(
        c => c.id === calendar.calendar_id
      )

      if (selectedCalendar) {
        selectedCalendar = {
          ...selectedCalendar,
          shared_mailbox_id: null,
        }
      }

      if (
        !selectedCalendar &&
        calendar.shared_mailbox_id &&
        connection?.shared_mailboxes
      ) {
        const mailbox = connection.shared_mailboxes.find(
          m => m.id === calendar.shared_mailbox_id
        )
        if (mailbox) {
          const cal = mailbox.calendars.find(c => c.id === calendar.calendar_id)
          if (cal) {
            selectedCalendar = {
              ...cal,
              name: `${cal.name} (${mailbox.email})`,
              shared_mailbox_id: mailbox.id,
            }
          }
        }
      }

      if (!selectedCalendar) {
        selectedCalendar = {
          id: calendar.calendar_id,
          name: '-',
          shared_mailbox_id: calendar.shared_mailbox_id,
        }
      }

      this.form = {
        ...calendar,
        service,
        account: calendar.external_calendar_connection.email,
        selectedCalendar,
        event_title_format: find(this.titleFormats, format => {
          return format.id === calendar.event_title_format
        }),
      }

      this.$modal.show('modal-company-calendar')
    },

    resetCompanyCalendarModal() {
      this.$nextTick(() => {
        this.form = {
          service: null,
          account: null,
          scopes: null,
          event_title_prefix: 'On leave:',
          event_title_format: null,
          leave_types: [],
          selectedCalendar: null,
        }

        this.$validator.reset()
        this.errors.clear()
      })
    },

    async submit() {
      await this.validate()

      if (!this.valid) return

      if (this.editing) {
        await this.updateCompanyCalendar()

        return
      }

      await this.createSynchronization()
    },

    async createSynchronization() {
      this.loading = true

      try {
        const connections = this.employmentExternalCalendarConnections.filterByProvider(
          this.form.service.provider
        )
        const connection = connections.find(
          connection => connection.email === this.form.account
        )

        await ExternalCalendars.create(this.activeCompany.id, {
          company_id: this.activeCompany.id,
          connection_id: connection.id,
          calendar: this.form.selectedCalendar.id,
          shared_mailbox_id: this.form.selectedCalendar.shared_mailbox_id,
          scopes: this.form.scopes,
          event_title_prefix: this.form.event_title_prefix,
          event_title_format: this.form.event_title_format.id,
          leave_types: this.form.leave_types,
        })

        this.success('Synchronization created successfully.')

        this.hideCompanyCalendarModal()

        await this.fetchCalendarSynchronizations()
      } catch ({ response }) {
        this.validateFromResponse(response)
      }

      this.loading = false
    },

    async updateCompanyCalendar() {
      try {
        this.loading = true

        await ExternalCalendars.updateCompanyCalendar(
          this.activeCompany,
          this.form.group,
          {
            company_id: this.activeCompany.id,
            scopes: this.form.scopes,
            event_title_prefix: this.form.event_title_prefix,
            event_title_format: this.form.event_title_format.id,
            calendar: this.form.selectedCalendar.id,
            leave_types: this.form.leave_types,
          }
        )

        this.$modal.hide('modal-company-calendar')

        await this.fetchCalendarSynchronizations()

        this.resetCompanyCalendarModal()

        this.success('Update successfully.')
      } catch ({ response }) {
        this.validateFromResponse(response)
      }

      this.loading = false
    },

    async fetchCalendarSynchronizations() {
      try {
        this.loadingCompanyCalendars = true

        this.calendarSynchronizations = await ExternalCalendars.getSynchronizations(
          this.activeCompany
        )

        this.loadingCompanyCalendars = false
      } catch ({ response }) {
        this.loadingCompanyCalendars = false
        this.validateFromResponse(response, false)
      }
    },

    async reconnect(connection) {
      try {
        this.reconnecting = true

        const redirectUrl =
          window.location.origin +
          this.$router.resolve({ name: 'integrations' }).href +
          `?company=${this.activeCompany.id}`

        const { data } = await ExternalCalendars.reconnect(
          this.activeCompany,
          connection,
          redirectUrl
        )

        window.location.href = data.redirect_url
      } catch ({ response }) {
        this.validateFromResponse(response)
      }

      this.reconnecting = false
    },

    async deleteCalendar(calendar) {
      const confirm = await this.confirmCalendarDisconnect(calendar)
      if (!confirm) return

      try {
        this.loading = true

        await ExternalCalendars.deleteCalendar(this.activeCompany, calendar)

        await this.fetchCalendarSynchronizations()

        this.success('Delete successfully.')
      } catch ({ response }) {
        this.validateFromResponse(response, false)
      }

      this.loading = false
    },

    async confirmCalendarDisconnect(calendar) {
      const connection = calendar.external_calendar_connection

      if (
        (!connection.is_active && connection.service_error) ||
        (calendar.service_error && !calendar.sync_enabled)
      ) {
        return await this.confirm(
          'Are you sure you want to delete? There are pending synchronisation jobs for this calendar. ' +
            'If you remove the integration now, we will not be able to sync the latest changes, ' +
            'which could result in problems such as duplicate events.',
          'Confirm delete'
        )
      }

      return await this.confirm(
        'Are you sure you want to delete?',
        'Confirm delete'
      )
    },

    async deleteConnection(connection) {
      const confirm = await this.confirm(
        'Are you sure you want to delete?',
        'Confirm delete'
      )

      if (!confirm) return

      this.deleting = true
      try {
        await ExternalCalendars.deleteConnection(
          this.activeEmployment,
          this.activeCompany,
          connection
        )

        await this.fetchEmploymentExternalCalendarConnections()
        await this.fetchCalendarSynchronizations()
      } catch ({ response }) {
        this.validateFromResponse(response, false)
      }

      this.deleting = false
    },

    getServiceNameByProvider(provider) {
      return find(this.services, service => {
        return service.provider === provider
      }).name
    },

    getScopesNames(scopes) {
      return reduce(
        scopes,
        (names, scope) => {
          if (scope === 'everyone') {
            names.push('Everyone')

            return names
          }

          let departmentInScope = this.departments.find(department => {
            return department.id === scope.split(':')[1]
          })

          if (departmentInScope) {
            names.push(departmentInScope.name)
          }

          return names
        },
        []
      )
    },

    getCalendarName(synchronization) {
      if (
        synchronization.external_calendar_connection.owner_id !==
        this.activeEmployment.id
      ) {
        return '-'
      }

      if (synchronization.calendar_id) {
        const connection = this.employmentExternalCalendarConnections.findById(
          synchronization.external_calendar_connection.id
        )

        if (synchronization.shared_mailbox_id && connection?.shared_mailboxes) {
          const mailbox = connection.shared_mailboxes.find(
            m => m.id === synchronization.shared_mailbox_id
          )
          const calendar = mailbox?.calendars?.find(
            c => c.id === synchronization.calendar_id
          )
          if (calendar) {
            return calendar.name
          }
        }

        const calendar = connection?.calendars?.find(
          calendar => synchronization.calendar_id === calendar.id
        )

        if (calendar) {
          return calendar.name
        }
      }

      if (
        synchronization.sync_enabled === false &&
        synchronization.service_error
      ) {
        return 'N/A'
      }

      return 'Primary'
    },

    showErrorMessage() {
      this.error(this.$route.query.error)

      this.resetRoute()
    },

    resetRoute() {
      this.$router.push({ name: 'integrations' }, () => {})
    },

    async addSharedMailbox(connection) {
      const email = this.sharedMailboxEmails[connection.id]
      if (!email) return

      this.addingSharedMailbox = true
      this.$set(this.sharedMailboxErrors, connection.id, null)

      try {
        await ExternalCalendars.addSharedMailbox(
          this.$apollo,
          this.activeCompany.id,
          connection.id,
          email
        )

        this.$set(this.sharedMailboxEmails, connection.id, '')
        await this.fetchEmploymentExternalCalendarConnections()
        this.success('Shared mailbox added successfully.')
      } catch (error) {
        const message =
          error.graphQLErrors?.[0]?.message ||
          error.message ||
          'Failed to add shared mailbox'
        this.$set(this.sharedMailboxErrors, connection.id, message)
      }

      this.addingSharedMailbox = false
    },

    async removeSharedMailbox(connection, mailbox) {
      const confirm = await this.confirm(
        'Are you sure you want to remove this shared mailbox? All synchronizations using this mailbox will be deleted.',
        'Confirm delete'
      )

      if (!confirm) return

      this.removingSharedMailbox = true

      try {
        await ExternalCalendars.removeSharedMailbox(
          this.$apollo,
          this.activeCompany.id,
          mailbox.id
        )

        await this.fetchEmploymentExternalCalendarConnections()
        await this.fetchCalendarSynchronizations()
        this.success('Shared mailbox removed successfully.')
      } catch (error) {
        const message =
          error.graphQLErrors?.[0]?.message ||
          error.message ||
          'Failed to remove shared mailbox'
        this.error(message)
      }

      this.removingSharedMailbox = false
    },
  },
}
</script>
